import {
  type CsvDataRow,
  type RawTelemetryData,
  type GpsReading,
  type Device,
  calculateDistance,
} from '@pitbrain/shared'
import useApi from '../../../api/useApi'
import ConfigurationContext from '../../../core/providers/ConfigurationContext'
import { useContext } from 'react'
import { useBaseProcessor } from '../useBaseProcessor'
import { type UploadFormat } from '../UploadFormat'

export const useCsvProcessor = (): UploadFormat => {
  const bffConfig = useContext(ConfigurationContext)
  const api = useApi(bffConfig)
  const { getFirstCoordinate, processAndCreateSession } = useBaseProcessor()
  const { error: apiError } = api

  const parseCsvToRawTelemetry = (results: CsvDataRow[]): RawTelemetryData => {
    let totalDistance = 0
    const gps: GpsReading[] = results.map((p: CsvDataRow, index, array) => {
      if (index > 0) {
        const prev = array[index - 1]
        totalDistance += calculateDistance(
          prev.latitude,
          prev.longitude,
          p.latitude,
          p.longitude
        )
      }

      return {
        latitude: p.latitude,
        longitude: p.longitude,
        altitude: undefined,
        speedMs: p.speed != null ? p.speed / 3.6 : undefined,
        timestamp: new Date(p.date),
        distanceMeters: totalDistance,
      }
    })

    return {
      gps,
      gyroscope: undefined,
      accelerometer: undefined,
      recordingStartTime: gps[0]?.timestamp,
      device: {
        deviceName: 'Unknown',
        deviceVersion: '1.0',
      },
    }
  }

  const processFile = async (file: File, sessionId: string) => {
    const results = await api.fileApi.convertCsvFile(file)
    if (results.length === 0) {
      return
    }

    const rawTelemetry = parseCsvToRawTelemetry(results)
    const firstCoordinate = getFirstCoordinate(rawTelemetry)
    if (firstCoordinate == null) return

    const track = await api.trackApi.findTrack(firstCoordinate)
    const device: Device = { name: 'Unknown', version: '1.0' }

    await processAndCreateSession(
      rawTelemetry,
      sessionId,
      track,
      { hasLeanAngle: false, hasVideo: false },
      file.name,
      device
    )
  }

  return {
    extensions: ['.CSV'],
    keepsFileAsSource: true,
    processFile,
    error: apiError,
  }
}
